import { DeleteOutlined, HolderOutlined, PlusOutlined } from "@ant-design/icons";
import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Form, Input } from "antd";
import { type FormInstance } from "antd/lib/form";

import { defaultFaqFor } from "@fitness-app/data-models/entities/CheckoutFaqDefaults";
import {
  type CheckoutLandingTemplate,
  type PageConfiguration,
} from "@fitness-app/data-models/entities/CheckoutVariant";

const FaqEntryRow = ({ id, name, onRemove }: { id: string; name: number; onRemove: () => void }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-start gap-2 rounded-md border border-solid border-gray-200 bg-white p-2 ${isDragging ? "relative z-10 shadow-lg" : ""}`}
    >
      <Button
        ref={setActivatorNodeRef}
        type="text"
        size="small"
        aria-label="Przeciągnij pytanie"
        icon={<HolderOutlined />}
        className="mt-1 cursor-grab touch-none"
        {...attributes}
        {...listeners}
      />
      <div className="flex flex-1 flex-col gap-2">
        <Form.Item name={[name, "question"]} className="!mb-0">
          <Input placeholder="Pytanie" />
        </Form.Item>
        <Form.Item name={[name, "answer"]} className="!mb-0">
          <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Odpowiedź" />
        </Form.Item>
      </div>
      <Button type="text" danger icon={<DeleteOutlined />} aria-label="Usuń pytanie" onClick={onRemove} />
    </div>
  );
};

/**
 * FAQ wariantu w szablonach Hollywood: pytania z odpowiedziami, kolejność przeciąganiem.
 * Pusta lista = wariant pokazuje domyślne pytania szablonu (zapis pomija puste FAQ), a przycisk
 * „Wczytaj domyślne pytania” daje je jako punkt wyjścia do edycji.
 */
export const ProductPageFaqField = ({
  formController,
  landingTemplate,
  isRenewal,
}: {
  formController: FormInstance<Omit<PageConfiguration, "pricing"> & { showPromoVideo: boolean }>;
  landingTemplate: CheckoutLandingTemplate | undefined;
  isRenewal: boolean;
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  return (
    <Form.Item
      label="FAQ"
      tooltip="Pytania pod koszykiem. Przeciągnij za uchwyt, żeby zmienić kolejność. Pusta lista = domyślne pytania szablonu."
    >
      <Form.Item name={["faq", "title"]} className="!mb-2">
        <Input placeholder="Nagłówek, domyślnie: Najczęściej zadawane pytania" />
      </Form.Item>
      <Form.List name={["faq", "entries"]}>
        {(fields, { add, remove, move }) => {
          const ids = fields.map((field) => String(field.key));
          const handleDragEnd = ({ active, over }: DragEndEvent) => {
            if (over && active.id !== over.id) {
              move(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
            }
          };

          return (
            <div className="flex flex-col gap-2">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                  {fields.map((field) => (
                    <FaqEntryRow
                      key={field.key}
                      id={String(field.key)}
                      name={field.name}
                      onRemove={() => remove(field.name)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <div className="flex gap-2">
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ question: "", answer: "" })}>
                  Dodaj pytanie
                </Button>
                {fields.length === 0 ? (
                  <Button
                    onClick={() =>
                      formController.setFieldsValue({ faq: structuredClone(defaultFaqFor(landingTemplate, isRenewal)) })
                    }
                  >
                    Wczytaj domyślne pytania
                  </Button>
                ) : null}
              </div>
            </div>
          );
        }}
      </Form.List>
    </Form.Item>
  );
};
